import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  MapPin,
  Globe,
  Phone,
  Play,
  Square,
  RefreshCw,
  Search,
  ExternalLink,
  ChevronRight,
  Archive,
  Trash2,
  Lock,
  Compass,
  FileText,
  Clock,
  PlusCircle,
  Building2,
  Zap,
  Copy,
  Check,
  Code,
  Cpu,
  Wand2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { realtimeDbService as dbService } from '../../services/realtimeDbService';
import { ShopierModal } from '../billing/ShopierModal';
import { AlertDialog } from '../common/AlertDialog';
import { getSiteDisplayAddress } from '../../utils/security';
import {
  AILocalVisibilityEntity,
  AIVisibilityAnalysisRun,
  AIVisibilitySubscription,
  SiteData,
} from '../../types';

interface AIVisibilityViewProps {
  onNavigate?: (view: string) => void;
}

export const AIVisibilityView: React.FC<AIVisibilityViewProps> = ({ onNavigate }) => {
  const { currentUser, isSuperAdmin } = useAuth();
  const tenantId = currentUser?.uid || '';

  // Core States
  const [loading, setLoading] = useState<boolean>(true);
  const [subscription, setSubscription] = useState<AIVisibilitySubscription | null>(null);
  const [activeEntity, setActiveEntity] = useState<AILocalVisibilityEntity | null>(null);
  const [allEntities, setAllEntities] = useState<AILocalVisibilityEntity[]>([]);
  const [runs, setRuns] = useState<AIVisibilityAnalysisRun[]>([]);
  const [latestRun, setLatestRun] = useState<AIVisibilityAnalysisRun | null>(null);
  const [userSites, setUserSites] = useState<SiteData[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');

  // Execution & Action States
  const [isRunningAnalysis, setIsRunningAnalysis] = useState<boolean>(false);
  const [isPreparingTest, setIsPreparingTest] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Optimization Agent Action States
  const [copiedRecId, setCopiedRecId] = useState<string | null>(null);
  const [applyingRecId, setApplyingRecId] = useState<string | null>(null);
  const [validatingRecId, setValidatingRecId] = useState<string | null>(null);

  // Central Alert Dialog States
  const [deleteAlertOpen, setDeleteAlertOpen] = useState<boolean>(false);
  const [isDeletingEntity, setIsDeletingEntity] = useState<boolean>(false);
  const [archiveAlertOpen, setArchiveAlertOpen] = useState<boolean>(false);
  const [isArchivingEntity, setIsArchivingEntity] = useState<boolean>(false);
  const [secondBusinessAlertOpen, setSecondBusinessAlertOpen] = useState<boolean>(false);
  const [runningTaskAlertOpen, setRunningTaskAlertOpen] = useState<boolean>(false);
  const [timeoutAlertOpen, setTimeoutAlertOpen] = useState<boolean>(false);
  const [apiErrorAlertOpen, setApiErrorAlertOpen] = useState<boolean>(false);
  const [apiErrorMessage, setApiErrorMessage] = useState<string>('');
  const [applyConfirmAlertOpen, setApplyConfirmAlertOpen] = useState<boolean>(false);
  const [pendingApplyRecId, setPendingApplyRecId] = useState<string | null>(null);

  // Modals & Panels
  const [isCheckoutOpen, setIsCheckoutOpen] = useState<boolean>(false);
  const [showEntityModal, setShowEntityModal] = useState<boolean>(false);
  const [entityModalMode, setEntityModalMode] = useState<'create' | 'edit'>('create');
  const [activeViewTab, setActiveViewTab] = useState<'dashboard' | 'tests' | 'technical' | 'recommendations' | 'history'>('dashboard');

  // Entity Form State
  const [formBusinessName, setFormBusinessName] = useState<string>('');
  const [formWebsiteUrl, setFormWebsiteUrl] = useState<string>('');
  const [formGoogleMapsUrl, setFormGoogleMapsUrl] = useState<string>('');
  const [formPhone, setFormPhone] = useState<string>('');
  const [formEmail, setFormEmail] = useState<string>('');
  const [formCity, setFormCity] = useState<string>('');
  const [formDistrict, setFormDistrict] = useState<string>('');
  const [formAddress, setFormAddress] = useState<string>('');
  const [formServices, setFormServices] = useState<string>('');
  const [formDescription, setFormDescription] = useState<string>('');

  // 1. Initial Data Loader
  const loadData = async () => {
    if (!tenantId) return;
    setLoading(true);
    setActionError(null);
    try {
      const [sub, entity, entities, runList, sites] = await Promise.all([
        dbService.getAIVisibilitySubscription(tenantId),
        dbService.getActiveAILocalEntity(tenantId),
        dbService.getAllAILocalEntities(tenantId),
        dbService.getAIVisibilityRuns(tenantId),
        dbService.getSitesByUser(tenantId),
      ]);

      setSubscription(sub);
      setActiveEntity(entity);
      setAllEntities(entities);
      setRuns(runList);
      setUserSites(sites || []);
      if (sites && sites.length > 0 && !selectedSiteId) {
        setSelectedSiteId(sites[0].id);
      }

      if (runList.length > 0) {
        setLatestRun(runList[0]);
        // If the newest run is still in RUNNING status, mark executing
        if (runList[0].status === 'RUNNING') {
          setIsRunningAnalysis(true);
        } else {
          setIsRunningAnalysis(false);
        }
      } else {
        setLatestRun(null);
      }
    } catch (err: any) {
      console.error('Error loading AI visibility state:', err);
      setActionError('Bilgiler yüklenirken bir sorun oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (recId: string, snippet?: string) => {
    if (!snippet) return;
    navigator.clipboard.writeText(snippet);
    setCopiedRecId(recId);
    setTimeout(() => setCopiedRecId(null), 2500);
  };

  const handleApplyRecommendation = async (recId: string) => {
    if (!latestRun) return;
    setApplyingRecId(recId);
    setActionError(null);
    setActionSuccess(null);
    try {
      const res = await fetch('/api/ai-visibility/apply-recommendation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          runId: latestRun.runId,
          recommendationId: recId,
          siteId: selectedSiteId || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Öneri uygulanamadı.');
      }
      setLatestRun((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          recommendations: prev.recommendations.map((r) =>
            r.id === recId
              ? {
                  ...r,
                  isApplied: true,
                  appliedAt: Date.now(),
                  appliedSiteId: selectedSiteId,
                  validationResult: {
                    valid: true,
                    message: 'Değişiklik web sitenize başarıyla uygulandı ve yapılandırılmış kod entegre edildi.',
                    testedAt: Date.now(),
                  },
                }
              : r
          ),
        };
      });
      setActionSuccess('Değişiklik web sitenize başarıyla uygulandı ve yapılandırılmış veri entegre edildi.');
      setTimeout(() => setActionSuccess(null), 5000);
    } catch (err: any) {
      setActionError(err.message || 'Öneri uygulanırken hata oluştu.');
    } finally {
      setApplyingRecId(null);
    }
  };

  const handleValidateRecommendation = async (recId: string) => {
    if (!latestRun) return;
    setValidatingRecId(recId);
    setActionError(null);
    setActionSuccess(null);
    try {
      const res = await fetch('/api/ai-visibility/validate-recommendation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          runId: latestRun.runId,
          recommendationId: recId,
          siteUrl: activeEntity?.websiteUrl,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Doğrulama başarısız.');
      }
      setLatestRun((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          recommendations: prev.recommendations.map((r) =>
            r.id === recId ? { ...r, validationResult: data.validationResult } : r
          ),
        };
      });
      setActionSuccess('Doğrulama başarılı: Yapılandırılmış veri standartlara uygun olarak doğrulandı.');
      setTimeout(() => setActionSuccess(null), 5000);
    } catch (err: any) {
      setActionError(err.message || 'Doğrulama gerçekleştirilemedi.');
    } finally {
      setValidatingRecId(null);
    }
  };

  useEffect(() => {
    loadData();
  }, [tenantId]);

  // Periodic Poller when analysis is active
  useEffect(() => {
    if (!isRunningAnalysis || !tenantId) return;
    const interval = setInterval(async () => {
      try {
        const runList = await dbService.getAIVisibilityRuns(tenantId);
        setRuns(runList);
        if (runList.length > 0) {
          const newest = runList[0];
          setLatestRun(newest);
          if (newest.status !== 'RUNNING') {
            setIsRunningAnalysis(false);
            if (newest.status === 'COMPLETED') {
              setActionSuccess('AI Yerel Görünürlük analizi başarıyla tamamlandı!');
            } else if (newest.status === 'ABORTED') {
              setTimeoutAlertOpen(true);
            } else if (newest.status === 'FAILED') {
              setApiErrorMessage(newest.errorMessage || 'Analiz tamamlanamadı.');
              setApiErrorAlertOpen(true);
            }
          }
        }
      } catch (err) {
        console.warn('Polling error:', err);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [isRunningAnalysis, tenantId]);

  const hasAccess = Boolean(isSuperAdmin || (subscription && subscription.status === 'active' && subscription.endDate > Date.now()));

  // 2. Start AI Local Visibility Analysis
  const handleStartAnalysis = async () => {
    if (!activeEntity) {
      setActionError('Lütfen önce işletme bilgilerinizi tanımlayın.');
      return;
    }
    if (isRunningAnalysis || isPreparingTest) {
      setRunningTaskAlertOpen(true);
      return;
    }

    setIsPreparingTest(true);
    setActionError(null);
    setActionSuccess(null);

    try {
      const res = await fetch('/api/ai-visibility/run-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          userId: tenantId,
          entityId: activeEntity.id,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        setIsRunningAnalysis(false);
        if (data.error === 'ALREADY_RUNNING') {
          setRunningTaskAlertOpen(true);
        } else if (data.error === 'AI_LOCAL_VISIBILITY_REQUIRED') {
          setIsCheckoutOpen(true);
        } else {
          setApiErrorMessage(data.message || 'Analiz başlatılamadı. Lütfen kısa bir süre sonra tekrar deneyiniz.');
          setApiErrorAlertOpen(true);
        }
        return;
      }

      setIsRunningAnalysis(true);
      setLatestRun(data.run);
      // Wait for pollers to update
    } catch (err: any) {
      setIsRunningAnalysis(false);
      setApiErrorMessage(err?.message || 'Sunucu bağlantı hatası oluştu.');
      setApiErrorAlertOpen(true);
    } finally {
      setIsPreparingTest(false);
    }
  };

  // 3. Stop / Abort AI Analysis
  const handleStopAnalysis = async () => {
    if (!latestRun || latestRun.status !== 'RUNNING') return;
    try {
      const res = await fetch('/api/ai-visibility/stop-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          userId: tenantId,
          runId: latestRun.runId,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setIsRunningAnalysis(false);
        setActionError('Analiz başarıyla durduruldu (ABORTED).');
        await loadData();
      }
    } catch (err: any) {
      setActionError('Analiz durdurulurken hata oluştu.');
    }
  };

  // 4. Save or Update Business Entity (Enforces 1 Active Entity)
  const handleSaveEntity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formBusinessName.trim() || !formCity.trim()) {
      setActionError('İşletme adı ve şehir zorunludur.');
      return;
    }

    setActionError(null);
    const servicesArr = formServices.split(',').map((s) => s.trim()).filter(Boolean);

    const payload = {
      businessName: formBusinessName.trim(),
      legalName: formBusinessName.trim(),
      brandVariants: [formBusinessName.trim()],
      websiteUrl: formWebsiteUrl.trim(),
      googleMapsUrl: formGoogleMapsUrl.trim(),
      phone: formPhone.trim(),
      email: formEmail.trim(),
      city: formCity.trim(),
      district: formDistrict.trim(),
      address: formAddress.trim(),
      services: servicesArr.length > 0 ? servicesArr : ['Genel Hizmet'],
      serviceAreas: [formCity.trim()],
      socialProfiles: {},
      description: formDescription.trim() || `${formBusinessName} yerel kurumsal hizmetler.`,
      primaryKeywords: servicesArr,
      faq: [],
      importantFacts: ['Resmi İşletme'],
    };

    if (entityModalMode === 'create') {
      // Check 1 Active Entity Constraint
      if (activeEntity) {
        setActionError('Bu abonelikte zaten aktif bir işletme bulunuyor. Yeni işletme eklemek için mevcut olanı arşivleyin veya silin.');
        return;
      }

      try {
        const res = await fetch('/api/ai-visibility/entity', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenantId,
            userId: tenantId,
            entityData: payload,
          }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          setActionError(data.error || 'İşletme oluşturulamadı.');
          return;
        }
        setActionSuccess('İşletme başarıyla oluşturuldu.');
        setShowEntityModal(false);
        await loadData();
      } catch (err: any) {
        setActionError('İşletme kaydedilemedi.');
      }
    } else if (entityModalMode === 'edit' && activeEntity) {
      try {
        const res = await fetch(`/api/ai-visibility/entity/${activeEntity.id}`, {
          method: 'put',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenantId,
            userId: tenantId,
            updates: payload,
          }),
        });
        const data = await res.json();
        if (data.success) {
          setActionSuccess('İşletme bilgileri güncellendi.');
          setShowEntityModal(false);
          await loadData();
        }
      } catch (err: any) {
        setActionError('Güncelleme başarısız oldu.');
      }
    }
  };

  // 5. Open Edit Modal
  const openEditModal = () => {
    if (!activeEntity) return;
    setEntityModalMode('edit');
    setFormBusinessName(activeEntity.businessName || '');
    setFormWebsiteUrl(activeEntity.websiteUrl || '');
    setFormGoogleMapsUrl(activeEntity.googleMapsUrl || '');
    setFormPhone(activeEntity.phone || '');
    setFormEmail(activeEntity.email || '');
    setFormCity(activeEntity.city || '');
    setFormDistrict(activeEntity.district || '');
    setFormAddress(activeEntity.address || '');
    setFormServices(activeEntity.services?.join(', ') || '');
    setFormDescription(activeEntity.description || '');
    setShowEntityModal(true);
  };

  // 6. Open Create Modal
  const openCreateModal = () => {
    if (activeEntity) {
      setActionError('Bu abonelikte zaten aktif bir işletme bulunuyor. Yeni bir işletme eklemek için mevcut işletmeyi arşivleyin, silin veya ikinci bir abonelik satın alın.');
      return;
    }
    setEntityModalMode('create');
    setFormBusinessName('');
    setFormWebsiteUrl('');
    setFormGoogleMapsUrl('');
    setFormPhone('');
    setFormEmail('');
    setFormCity('');
    setFormDistrict('');
    setFormAddress('');
    setFormServices('');
    setFormDescription('');
    setShowEntityModal(true);
  };

  // 7. Archive Active Entity
  const handleArchiveEntity = () => {
    if (!activeEntity) return;
    setArchiveAlertOpen(true);
  };

  const handleConfirmArchiveEntity = async () => {
    if (!activeEntity) return;
    setIsArchivingEntity(true);
    try {
      const res = await fetch(`/api/ai-visibility/entity/${activeEntity.id}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, userId: tenantId }),
      });
      const data = await res.json();
      if (data.success) {
        setActionSuccess('İşletme arşivlendi. Artık yeni bir işletme tanımlayabilirsiniz.');
        await loadData();
      } else {
        setActionError(data.error || 'İşletme arşivlenemedi.');
      }
    } catch {
      setActionError('İşletme arşivlenemedi.');
    } finally {
      setIsArchivingEntity(false);
      setArchiveAlertOpen(false);
    }
  };

  // 8. Delete Active Entity: Open Confirmation Dialog
  const handleDeleteEntity = () => {
    if (!activeEntity) return;
    setDeleteAlertOpen(true);
  };

  const handleConfirmDeleteEntity = async () => {
    if (!activeEntity) return;
    setIsDeletingEntity(true);
    setActionError(null);
    try {
      // 1. Server-side deletion via API
      const res = await fetch(`/api/ai-visibility/entity/${activeEntity.id}?tenantId=${tenantId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();

      // 2. Client local cache & RTDB sync
      await dbService.deleteAILocalEntity(tenantId, activeEntity.id);

      // 3. Immediately clear state in component
      setActiveEntity(null);
      setAllEntities((prev) => prev.filter((e) => e.id !== activeEntity.id));

      if (data.success) {
        setActionSuccess('İşletme başarıyla silindi ve aktif AI Görünürlük hakkı serbest bırakıldı. Yeni bir işletme tanımlayabilirsiniz.');
        await loadData();
      } else {
        throw new Error(data.error || 'İşletme silinemedi.');
      }
    } catch (err: any) {
      console.error('Delete entity error:', err);
      setActionError(err?.message || 'İşletme silinemedi.');
    } finally {
      setIsDeletingEntity(false);
      setDeleteAlertOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="customer-shell min-h-[500px] flex flex-col items-center justify-center p-8 bg-slate-50 dark:bg-slate-900 rounded-2xl">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin mb-3" />
        <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
          AI Yerel Görünürlük ve Varlık Verileri Yükleniyor...
        </p>
      </div>
    );
  }

  return (
    <div className="ai-visibility-shell space-y-6">
      {/* Notifications */}
      {actionError && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 flex items-start justify-between shadow-sm animate-fadeIn">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-600" />
            <span className="text-sm font-medium">{actionError}</span>
          </div>
          <button onClick={() => setActionError(null)} className="text-red-500 hover:text-red-700 text-sm font-bold">
            ×
          </button>
        </div>
      )}

      {actionSuccess && (
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 flex items-start justify-between shadow-sm animate-fadeIn">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-600" />
            <span className="text-sm font-medium">{actionSuccess}</span>
          </div>
          <button onClick={() => setActionSuccess(null)} className="text-emerald-600 hover:text-emerald-800 text-sm font-bold">
            ×
          </button>
        </div>
      )}

      {/* HEADER SECTION: Product Identity & Status */}
      <div className="ai-card-dark bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] items-start lg:items-center gap-6 relative z-10">
          <div className="space-y-2 min-w-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-xs font-semibold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
              <span>AI Arama & Mekan Görünürlüğü (AI_LOCAL_VISIBILITY)</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white break-words">
              AI Arama & Mekan Görünürlüğü
            </h1>
            <p className="text-sm sm:text-base text-slate-300 max-w-2xl break-words">
              ChatGPT, Gemini & Perplexity Canlı Görünürlük — İşletmenizi Google Arama, Gemini, ChatGPT ve Haritalar yapay zeka sistemlerinde görünür kılın; gerçek arama testleriyle bulunabilirlik durumunuzu doğrulayın.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
            {!hasAccess ? (
              <button
                type="button"
                onClick={() => setIsCheckoutOpen(true)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-slate-950 font-bold text-sm shadow-lg shadow-orange-500/25 transition-all transform hover:-translate-y-0.5"
              >
                <Zap className="w-4 h-4 text-slate-950 fill-current" />
                <span>Hemen Başla (5.000 TL / Yıl)</span>
              </button>
            ) : (
              <div className="flex items-center gap-2">
                {isRunningAnalysis ? (
                  <button
                    type="button"
                    onClick={handleStopAnalysis}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm shadow-md transition-all animate-pulse"
                  >
                    <Square className="w-4 h-4 fill-current" />
                    <span>Analizi Durdur</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleStartAnalysis}
                    disabled={!activeEntity || isRunningAnalysis || isPreparingTest}
                    className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-sm shadow-lg transition-all ${
                      activeEntity && !isPreparingTest
                        ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-600/30 transform hover:-translate-y-0.5 cursor-pointer'
                        : 'bg-slate-700 text-slate-400 cursor-not-allowed opacity-70'
                    }`}
                  >
                    <Play className="w-4 h-4 fill-current shrink-0" />
                    <span>
                      {isPreparingTest
                        ? 'Test hazırlanıyor...'
                        : isRunningAnalysis
                        ? 'Analiz Yürütülüyor...'
                        : 'AI Görünürlüğünü Test Et'}
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Subscription Meta Bar */}
        <div className="mt-6 pt-6 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-4 text-xs text-slate-400">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${hasAccess ? 'bg-emerald-400' : 'bg-amber-400'}`}></span>
              Durum: <strong className="text-white">{hasAccess ? 'Aktif Abonelik' : 'Abonelik Gerekli'}</strong>
            </span>
            {subscription && (
              <span>
                Bitiş:{' '}
                <strong className="text-slate-200">
                  {new Date(subscription.endDate).toLocaleDateString('tr-TR')}
                </strong>
              </span>
            )}
            <span className="hidden sm:inline">Paket: 5.000 TL / Yıl</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-slate-400">
              Aktif İşletme: <strong className="text-indigo-300">{activeEntity ? '1 / 1' : '0 / 1'}</strong>
            </span>
          </div>
        </div>
      </div>

      {/* BUSINESS ENTITY CARD (1 ACTIVE ENTITY CONSTRAINT) */}
      <div className="ai-card-white bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 shrink-0">
              <Building2 className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold text-slate-900">Aktif İşletme Varlığı</h2>
                <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-700">
                  Maksimum 1 İşletme Sınırı
                </span>
              </div>
              <p className="text-xs text-slate-500 truncate">
                Bu abonelik tek bir fiziksel işletme veya mekanın AI arama dizinini yönetir.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {activeEntity ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setSecondBusinessAlertOpen(true);
                  }}
                  className="px-3.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-semibold text-slate-600 transition flex items-center gap-1.5 cursor-pointer"
                >
                  <PlusCircle className="w-3.5 h-3.5 text-slate-400" />
                  <span>Yeni İşletme Ekle</span>
                </button>
                <button
                  type="button"
                  onClick={openEditModal}
                  className="px-3.5 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-xs font-semibold text-slate-700 transition"
                >
                  Bilgileri Düzenle
                </button>
                <button
                  type="button"
                  onClick={handleArchiveEntity}
                  title="İşletmeyi Arşivle"
                  className="p-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-600 transition"
                >
                  <Archive className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleDeleteEntity}
                  title="İşletmeyi Sil"
                  className="p-1.5 rounded-lg border border-red-200 hover:bg-red-50 text-red-600 transition"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            ) : null}
          </div>
        </div>

        {activeEntity ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-5 text-sm">
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 min-w-0">
              <span className="text-xs text-slate-400 block mb-1">İşletme / Marka Adı</span>
              <span className="font-bold text-slate-900 text-base truncate block">{activeEntity.businessName}</span>
              <span className="text-xs text-indigo-600 block mt-1 truncate">
                {activeEntity.city} {activeEntity.district ? `/ ${activeEntity.district}` : ''}
              </span>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 min-w-0">
              <span className="text-xs text-slate-400 block mb-1">Resmi Web Sitesi</span>
              {activeEntity.websiteUrl ? (
                <a
                  href={activeEntity.websiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-indigo-600 hover:underline flex items-center gap-1 truncate"
                >
                  <Globe className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{activeEntity.websiteUrl}</span>
                </a>
              ) : (
                <span className="text-amber-500 text-xs font-medium">Belirtilmedi</span>
              )}
              <span className="text-[11px] text-slate-400 block mt-1">Teknik sinyal kaynağı</span>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 min-w-0">
              <span className="text-xs text-slate-400 block mb-1">Google Haritalar & Telefon</span>
              <div className="flex items-center gap-1.5 text-slate-800 font-medium truncate">
                <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="truncate">{activeEntity.phone || 'Telefon yok'}</span>
              </div>
              {activeEntity.googleMapsUrl ? (
                <a
                  href={activeEntity.googleMapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-emerald-600 hover:underline flex items-center gap-1 mt-1 truncate"
                >
                  <MapPin className="w-3 h-3 shrink-0" />
                  <span className="truncate">Harita Profili Açık</span>
                </a>
              ) : (
                <span className="text-[11px] text-slate-400 block mt-1">Harita URL eklenmedi</span>
              )}
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 min-w-0">
              <span className="text-xs text-slate-400 block mb-1">Hedef Hizmetler</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {activeEntity.services && activeEntity.services.length > 0 ? (
                  activeEntity.services.slice(0, 3).map((srv, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[11px] font-medium truncate max-w-full"
                    >
                      {srv}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-slate-400">Hizmet tanımlanmadı</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* RESPONSIVE BUSINESS PROFILE FORM (Directly Visible When No Entity Configured) */
          <div className="mt-5 space-y-4">
            <div className="p-4 rounded-xl bg-indigo-50/60 border border-indigo-100 text-indigo-950 text-xs flex items-start gap-3">
              <Sparkles className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
              <div>
                <strong className="font-bold">AI Görünürlüğü İçin İşletmenizi Tanımlayın:</strong>
                <p className="mt-0.5 text-indigo-900/80">
                  Google Gemini, Haritalar ve ChatGPT sistemlerinde doğru indekslenmek için işletmenizin resmi adını, şehir/ilçe konumunu ve web sitesini eksiksiz girin.
                </p>
              </div>
            </div>

            <form onSubmit={handleSaveEntity} className="ai-form-grid text-sm pt-2">
              <div className="ai-form-field-full">
                <label>İşletme / Mekan Adı *</label>
                <input
                  type="text"
                  required
                  placeholder="Örn: Akdeniz Yatçılık & Marin Hizmetleri"
                  value={formBusinessName}
                  onChange={(e) => setFormBusinessName(e.target.value)}
                />
              </div>

              <div className="ai-form-field">
                <label>Şehir *</label>
                <input
                  type="text"
                  required
                  placeholder="Örn: Muğla"
                  value={formCity}
                  onChange={(e) => setFormCity(e.target.value)}
                />
              </div>

              <div className="ai-form-field">
                <label>İlçe / Bölge</label>
                <input
                  type="text"
                  placeholder="Örn: Bodrum"
                  value={formDistrict}
                  onChange={(e) => setFormDistrict(e.target.value)}
                />
              </div>

              <div className="ai-form-field">
                <label>Resmi Web Sitesi</label>
                <input
                  type="url"
                  placeholder="https://example.com"
                  value={formWebsiteUrl}
                  onChange={(e) => setFormWebsiteUrl(e.target.value)}
                />
              </div>

              <div className="ai-form-field">
                <label>Telefon Numarası</label>
                <input
                  type="tel"
                  placeholder="+90 555 123 45 67"
                  value={formPhone}
                  onChange={(e) => setFormPhone(e.target.value)}
                />
              </div>

              <div className="ai-form-field-full">
                <label>Google Haritalar Profil URL'si</label>
                <input
                  type="url"
                  placeholder="https://maps.app.goo.gl/..."
                  value={formGoogleMapsUrl}
                  onChange={(e) => setFormGoogleMapsUrl(e.target.value)}
                />
              </div>

              <div className="ai-form-field-full">
                <label>Verilen Hizmetler (Virgülle ayırın)</label>
                <input
                  type="text"
                  placeholder="Örn: Yat Kiralama, Mavi Tur, Kaptanlı Gulet Turu"
                  value={formServices}
                  onChange={(e) => setFormServices(e.target.value)}
                />
              </div>

              <div className="ai-form-field-full">
                <label>Kısa Açıklama</label>
                <textarea
                  rows={3}
                  placeholder="İşletmenin sunduğu ayrıcalıklar, deneyim ve hizmet detayları..."
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                />
              </div>

              <div className="ai-form-field-full flex items-center justify-end gap-3 pt-2">
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm shadow-md transition"
                >
                  İşletmeyi Kaydet ve Doğrula
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* RUN PROGRESS OR LATEST RESULTS */}
      {isRunningAnalysis && (
        <div className="bg-indigo-50/80 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900/50 rounded-2xl p-6 text-center shadow-sm">
          <div className="flex items-center justify-center gap-3 mb-3">
            <RefreshCw className="w-6 h-6 text-indigo-600 animate-spin" />
            <h3 className="text-base font-bold text-indigo-900 dark:text-indigo-200">
              AI Yerel Görünürlük Analizi Çalışıyor...
            </h3>
          </div>
          <p className="text-xs text-indigo-700 dark:text-indigo-300 max-w-lg mx-auto mb-4">
            Teknik web sinyalleri taranıyor, Gemini ve Google Arama grounding testleri yapılıyor,
            işletmenizin bulunabilirliği ve kaynak domainleri doğrulanıyor.
          </p>
          <button
            onClick={handleStopAnalysis}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold shadow transition"
          >
            <Square className="w-3.5 h-3.5 fill-current" />
            Analizi Durdur
          </button>
        </div>
      )}

      {/* DASHBOARD TABS */}
      {latestRun && (
        <div className="space-y-6">
          {/* NAVIGATION TABS */}
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2 overflow-x-auto text-sm">
            <button
              onClick={() => setActiveViewTab('dashboard')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                activeViewTab === 'dashboard'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              Genel Bakış
            </button>
            <button
              onClick={() => setActiveViewTab('tests')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                activeViewTab === 'tests'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              Arama Testleri ({latestRun.searchTests?.length || 0})
            </button>
            <button
              onClick={() => setActiveViewTab('technical')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                activeViewTab === 'technical'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              Teknik Web Kontrolleri
            </button>
            <button
              onClick={() => setActiveViewTab('recommendations')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                activeViewTab === 'recommendations'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              Öneriler & Eylemler ({latestRun.recommendations?.length || 0})
            </button>
            <button
              onClick={() => setActiveViewTab('history')}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                activeViewTab === 'history'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              Test Geçmişi ({runs.length})
            </button>
          </div>

          {/* TAB 1: OVERVIEW DASHBOARD */}
          {activeViewTab === 'dashboard' && (
            <div className="space-y-6">
              {/* METRIC SCORE CARDS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="ai-card-white bg-white border border-slate-200 rounded-2xl p-5 shadow-sm min-w-0">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                    Yapay Zeka Görünürlük Skoru
                  </span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-black text-indigo-600">
                      %{latestRun.overallScore}
                    </span>
                    <span className="text-xs text-slate-500 font-medium">/ 100</span>
                  </div>
                  <div className="w-full bg-slate-100 h-2 rounded-full mt-3 overflow-hidden">
                    <div
                      className="bg-indigo-600 h-full rounded-full transition-all duration-500"
                      style={{ width: `${latestRun.overallScore}%` }}
                    ></div>
                  </div>
                </div>

                <div className="ai-card-white bg-white border border-slate-200 rounded-2xl p-5 shadow-sm min-w-0">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                    Bulunabilirlik Oranı
                  </span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-black text-emerald-600">
                      %{latestRun.visibilityRatio}
                    </span>
                    <span className="text-xs text-slate-500">
                      ({latestRun.foundCount} / {latestRun.totalTests} sorgu)
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-3">
                    Arama botları ve yapay zeka tarafından doğrudan referans gösterilme.
                  </p>
                </div>

                <div className="ai-card-white bg-white border border-slate-200 rounded-2xl p-5 shadow-sm min-w-0">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                    Teknik Web Sağlığı
                  </span>
                  <div className="flex items-center gap-2 mt-1">
                    <ShieldCheck className="w-8 h-8 text-indigo-500 shrink-0" />
                    <div className="min-w-0">
                      <span className="text-base font-bold text-slate-900 block truncate">
                        {latestRun.technicalSignals?.robotsTxtAllowsAi ? 'Botlar Açık' : 'Kısıtlı'}
                      </span>
                      <span className="text-xs text-slate-500 block truncate">
                        Schema: {latestRun.technicalSignals?.localBusinessSchemaPresent ? 'Mevcut' : 'Eksik'}
                      </span>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-3 truncate">Robots.txt, Sitemap ve Schema uyumu.</p>
                </div>

                <div className="ai-card-white bg-white border border-slate-200 rounded-2xl p-5 shadow-sm min-w-0">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                    Son Test Zamanı
                  </span>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className="w-7 h-7 text-slate-400 shrink-0" />
                    <div>
                      <span className="text-sm font-bold text-slate-900 block">
                        {new Date(latestRun.completedAt || latestRun.startedAt).toLocaleDateString('tr-TR')}
                      </span>
                      <span className="text-xs text-slate-500 block">
                        {new Date(latestRun.completedAt || latestRun.startedAt).toLocaleTimeString('tr-TR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                  <span className="inline-block mt-3 px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-800">
                    Tamamlandı
                  </span>
                </div>
              </div>

              {/* ACTIONABLE RECOMMENDATIONS PREVIEW */}
              <div className="ai-card-white bg-white border border-slate-200 rounded-2xl p-6 shadow-sm min-w-0">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-500" />
                    Öncelikli Aksiyon Planı ve İyileştirmeler
                  </h3>
                  <button
                    onClick={() => setActiveViewTab('recommendations')}
                    className="text-xs font-semibold text-indigo-600 hover:underline flex items-center gap-1"
                  >
                    Tümünü Gör <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {latestRun.recommendations && latestRun.recommendations.length > 0 ? (
                    latestRun.recommendations.slice(0, 4).map((rec) => (
                      <div
                        key={rec.id}
                        className="p-4 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              rec.priority === 'HIGH'
                                ? 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300'
                            }`}
                          >
                            {rec.priority === 'HIGH' ? 'YÜKSEK ÖNCELİK' : 'ORTA ÖNCELİK'}
                          </span>
                          <span className="text-[11px] text-slate-400 font-mono">{rec.actionType}</span>
                        </div>
                        <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">{rec.title}</h4>
                        <p className="text-xs text-slate-600 dark:text-slate-400">{rec.description}</p>
                        <div className="p-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs font-medium text-indigo-700 dark:text-indigo-300">
                          {rec.suggestedAction}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-2 text-center py-6 text-sm text-slate-500">
                      İşletmeniz teknik ve arama sinyallerinde mükemmel uyum sergiliyor!
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: SEARCH QUERY TESTS */}
          {activeViewTab === 'tests' && (
            <div className="ai-card-white bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4 min-w-0 max-w-full">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">
                    Gerçek AI & Web Arama Test Sonuçları ve Kanıtları
                  </h3>
                  <p className="text-xs text-slate-500">
                    Gemini Google Grounding, OpenAI Web Search ve Haritalar arama motorlarında canlı bulunabilirlik kanıtları.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                    Çoklu Motor Doğrulaması
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                {latestRun.searchTests && latestRun.searchTests.length > 0 ? (
                  latestRun.searchTests.map((test) => {
                    const engineLabel =
                      test.engine === 'OPENAI_WEB_SEARCH'
                        ? 'OpenAI Web Search'
                        : test.engine === 'GOOGLE_MAPS_LOCAL'
                        ? 'Google Haritalar Yerel Arama'
                        : 'Gemini + Google Search Grounding';

                    const engineBadgeColor =
                      test.engine === 'OPENAI_WEB_SEARCH'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : test.engine === 'GOOGLE_MAPS_LOCAL'
                        ? 'bg-rose-50 text-rose-700 border-rose-200'
                        : 'bg-blue-50 text-blue-700 border-blue-200';

                    return (
                      <div
                        key={test.id}
                        className="p-5 rounded-xl border border-slate-200 bg-slate-50/70 space-y-3.5 min-w-0 max-w-full"
                      >
                        {/* Header Row: Provider, Query & Status */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                          <div className="flex items-center gap-2.5 min-w-0 flex-wrap">
                            <span
                              className={`px-2 py-0.5 rounded text-[11px] font-bold border ${engineBadgeColor} shrink-0`}
                            >
                              {engineLabel}
                            </span>
                            <span className="font-bold text-slate-900 text-sm truncate">
                              "{test.query}"
                            </span>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-200 text-slate-700">
                              {test.category}
                            </span>
                            <span
                              className={`px-2.5 py-0.5 rounded text-[11px] font-bold ${
                                test.status === 'FOUND'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : test.status === 'PARTIAL'
                                  ? 'bg-amber-100 text-amber-800'
                                  : test.status === 'UNAVAILABLE'
                                  ? 'bg-slate-200 text-slate-700'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {test.status === 'FOUND'
                                ? 'BULUNDU'
                                : test.status === 'PARTIAL'
                                ? 'KISMEN BULUNDU'
                                : test.status === 'UNAVAILABLE'
                                ? 'ERİŞİLEMEDİ (UNAVAILABLE)'
                                : 'BULUNAMADI'}
                            </span>
                          </div>
                        </div>

                        {/* Metadata: Date & Rank */}
                        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 border-b border-slate-200/60 pb-2">
                          <span>
                            Test Tarihi: <strong>{new Date(test.executedAt).toLocaleString('tr-TR')}</strong>
                          </span>
                          {test.positionRank && (
                            <span>
                              Sıralama / Konum: <strong className="text-slate-800">#{test.positionRank}</strong>
                            </span>
                          )}
                        </div>

                        {/* Summary / Reasoning */}
                        <p className="text-xs text-slate-700 bg-white p-3 rounded-lg border border-slate-200 break-words min-w-0">
                          {test.summary}
                        </p>

                        {/* Citation Snippet (if available) */}
                        {test.extractedSnippet && (
                          <div className="p-2.5 rounded-lg bg-indigo-50/50 border border-indigo-100 text-xs text-indigo-950 min-w-0">
                            <span className="font-semibold text-indigo-700 block text-[11px] mb-0.5">
                              Doğrudan Alıntı / Citation:
                            </span>
                            <p className="italic text-slate-700 break-words">"{test.extractedSnippet}"</p>
                          </div>
                        )}

                        {/* Cited Sources & Domains */}
                        <div className="space-y-1.5 pt-1 min-w-0">
                          <span className="text-[11px] font-semibold text-slate-500 block">
                            Kaynak URL & Referanslar:
                          </span>
                          {test.citedSources && test.citedSources.length > 0 ? (
                            <div className="flex flex-wrap gap-2 min-w-0">
                              {test.citedSources.map((src, idx) => (
                                <a
                                  key={idx}
                                  href={src.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition max-w-full truncate ${
                                    src.isEntityOwnedDomain
                                      ? 'bg-emerald-50 border-emerald-300 text-emerald-800 font-semibold shadow-xs'
                                      : 'bg-white border-slate-200 text-slate-700 hover:text-indigo-600 hover:border-indigo-300 shadow-xs'
                                  }`}
                                >
                                  <Globe className="w-3.5 h-3.5 shrink-0" />
                                  <span className="font-semibold truncate">{src.title || src.domain}</span>
                                  {src.isEntityOwnedDomain && (
                                    <span className="text-[10px] ml-1 bg-emerald-200 text-emerald-900 px-1.5 py-0.2 rounded font-bold shrink-0">
                                      Resmi Site
                                    </span>
                                  )}
                                  <ExternalLink className="w-3 h-3 ml-0.5 opacity-60 shrink-0" />
                                </a>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic">
                              Bu sorgu için yapay zeka tarafından doğrudan işletmeyle eşleşen harici citation tespit edilmedi.
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-slate-500 py-6 text-center">Bu analizde sorgu testi bulunamadı.</p>
                )}
              </div>
            </div>
          )}

          {/* TAB 3: TECHNICAL SIGNALS (CRAWLER AS SECONDARY UTILITY) */}
          {activeViewTab === 'technical' && (
            <div className="ai-card-white bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-5 min-w-0 max-w-full">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-bold text-slate-900">
                  Yardımcı Teknik Web Sinyalleri Denetimi
                </h3>
                <p className="text-xs text-slate-500">
                  Crawler'ın web sitesi üzerindeki kontrol sinyalleri (Start → Fetch → Analyze → Stop).
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/70 flex items-start gap-3 min-w-0">
                  {latestRun.technicalSignals?.websiteAccessible ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0">
                    <h4 className="text-sm font-bold text-slate-900">Web Sitesi Erişilebilirliği</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {latestRun.technicalSignals?.websiteAccessible
                        ? 'Resmi web sitesine HTTP isteği başarıyla ulaştı.'
                        : 'Web sitesi yanıt vermedi veya URL geçersiz.'}
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/70 flex items-start gap-3 min-w-0">
                  {latestRun.technicalSignals?.robotsTxtAllowsAi ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0">
                    <h4 className="text-sm font-bold text-slate-900">robots.txt AI Bot İzinleri</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {latestRun.technicalSignals?.robotsTxtAllowsAi
                        ? 'OpenAI (OAI-SearchBot) ve Google AI botlarına erişim açık.'
                        : 'robots.txt dosyasında bazı AI botları engellenmiş olabilir.'}
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/70 flex items-start gap-3 min-w-0">
                  {latestRun.technicalSignals?.localBusinessSchemaPresent ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0">
                    <h4 className="text-sm font-bold text-slate-900">LocalBusiness Schema.org</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {latestRun.technicalSignals?.localBusinessSchemaPresent
                        ? 'Web sitesinde resmi LocalBusiness JSON-LD şeması tespit edildi.'
                        : 'LocalBusiness şeması bulunamadı. Eklenmesi şiddetle önerilir.'}
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/70 flex items-start gap-3 min-w-0">
                  {latestRun.technicalSignals?.napConsistent ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0">
                    <h4 className="text-sm font-bold text-slate-900">NAP & Lokasyon Tutarlılığı</h4>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {latestRun.technicalSignals?.napConsistent
                        ? 'İşletme telefonu veya şehir bilgisi web sitesindeki verilerle eşleşiyor.'
                        : 'Web sitesindeki telefon/adres ile profil bilgileri örtüşmüyor.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: ACTIONABLE RECOMMENDATIONS & OPTIMIZATION AGENT */}
          {activeViewTab === 'recommendations' && (
            <div className="ai-card-white bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-5 min-w-0 max-w-full">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <Wand2 className="w-5 h-5 text-indigo-600" />
                    <span>AI Optimizasyon Ajanı & Uygulanabilir Kod Blokları</span>
                  </h3>
                  <p className="text-xs text-slate-500">
                    Eksik sinyalleri tek tıkla Adiosoft web sitenize entegre edin veya kodu kopyalayıp harici sitenize yapıştırın.
                  </p>
                </div>
                {userSites && userSites.length > 0 && (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-slate-500 font-medium">Hedef Site:</span>
                    <select
                      value={selectedSiteId}
                      onChange={(e) => setSelectedSiteId(e.target.value)}
                      className="text-xs bg-slate-50 border border-slate-300 rounded-lg px-2.5 py-1.5 font-semibold text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                    >
                      {userSites.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({getSiteDisplayAddress(s)})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                {latestRun.recommendations && latestRun.recommendations.length > 0 ? (
                  latestRun.recommendations.map((rec) => (
                    <div
                      key={rec.id}
                      className="p-5 rounded-2xl border border-slate-200 bg-slate-50/80 space-y-4 min-w-0 max-w-full shadow-xs"
                    >
                      {/* Status Badges Row */}
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2.5 py-0.5 rounded text-xs font-bold ${
                              rec.priority === 'HIGH'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {rec.priority === 'HIGH' ? 'YÜKSEK ÖNCELİK' : 'ORTA ÖNCELİK'}
                          </span>
                          <span className="px-2.5 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-800">
                            {rec.actionType}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          {rec.isApplied && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-800">
                              <Check className="w-3.5 h-3.5" />
                              <span>Web Sitesine Uygulandı</span>
                            </span>
                          )}
                          {rec.validationResult?.valid && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-800">
                              <ShieldCheck className="w-3.5 h-3.5" />
                              <span>Doğrulandı</span>
                            </span>
                          )}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-base font-bold text-slate-900">{rec.title}</h4>
                        <p className="text-xs text-slate-600 mt-1">{rec.description}</p>
                      </div>

                      {/* NEDEN? (Why it matters) */}
                      {rec.whyItMatters && (
                        <div className="p-3.5 rounded-xl bg-blue-50/70 border border-blue-200/80 text-xs text-blue-950">
                          <strong className="font-bold block text-blue-900 mb-1">
                            💡 Neden Önemli?
                          </strong>
                          <p className="text-blue-900/90 leading-relaxed">{rec.whyItMatters}</p>
                        </div>
                      )}

                      {/* GEREKEN BİLGİLER & HEDEF KONUM */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        {rec.requiredInfo && rec.requiredInfo.length > 0 && (
                          <div className="p-3 rounded-xl bg-white border border-slate-200">
                            <span className="text-[11px] font-bold text-slate-500 block mb-1.5">
                              Gerekli Veri Alanları:
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                              {rec.requiredInfo.map((info, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px] font-medium"
                                >
                                  {info}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {rec.targetLocation && (
                          <div className="p-3 rounded-xl bg-white border border-slate-200">
                            <span className="text-[11px] font-bold text-slate-500 block mb-1">
                              Hedef Konum / Dosya:
                            </span>
                            <span className="font-mono text-slate-800 text-[11px] font-semibold break-all">
                              {rec.targetLocation}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* UYGULANACAK KOD (CANLI KOD BLOĞU) */}
                      {rec.codeSnippet && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                              <Code className="w-4 h-4 text-indigo-600" />
                              <span>Uygulanacak Kod Snippet'ı:</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => handleCopyCode(rec.id, rec.codeSnippet)}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold transition cursor-pointer"
                            >
                              {copiedRecId === rec.id ? (
                                <>
                                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                                  <span className="text-emerald-400">Kopyalandı!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-3.5 h-3.5" />
                                  <span>Kodu Kopyala</span>
                                </>
                              )}
                            </button>
                          </div>

                          <div className="relative">
                            <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl text-xs font-mono overflow-x-auto border border-slate-800 max-h-64 scrollbar-thin">
                              <code>{rec.codeSnippet}</code>
                            </pre>
                          </div>
                        </div>
                      )}

                      {/* ACTION CONTROLS: APPLY & VALIDATE */}
                      <div className="pt-2 border-t border-slate-200/80 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          {userSites && userSites.length > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                setPendingApplyRecId(rec.id);
                                setApplyConfirmAlertOpen(true);
                              }}
                              disabled={applyingRecId === rec.id || rec.isApplied}
                              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition shadow-sm ${
                                rec.isApplied
                                  ? 'bg-emerald-50 border border-emerald-300 text-emerald-700 cursor-default'
                                  : applyingRecId === rec.id
                                  ? 'bg-indigo-400 text-white cursor-wait'
                                  : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-600/20 cursor-pointer'
                              }`}
                            >
                              {applyingRecId === rec.id ? (
                                <>
                                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                  <span>Web Sitesine Uygulanıyor...</span>
                                </>
                              ) : rec.isApplied ? (
                                <>
                                  <Check className="w-3.5 h-3.5" />
                                  <span>Web Sitesine Uygulandı</span>
                                </>
                              ) : (
                                <>
                                  <Cpu className="w-3.5 h-3.5" />
                                  <span>Adiosoft Web Sitesine Uygula</span>
                                </>
                              )}
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => handleValidateRecommendation(rec.id)}
                            disabled={validatingRecId === rec.id}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-semibold transition cursor-pointer"
                          >
                            {validatingRecId === rec.id ? (
                              <>
                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                <span>Doğrulanıyor...</span>
                              </>
                            ) : (
                              <>
                                <ShieldCheck className="w-3.5 h-3.5 text-indigo-600" />
                                <span>Doğrula & Test Et</span>
                              </>
                            )}
                          </button>
                        </div>

                        {rec.validationResult && (
                          <span
                            className={`text-xs font-semibold ${
                              rec.validationResult.valid ? 'text-emerald-600' : 'text-amber-600'
                            }`}
                          >
                            {rec.validationResult.message}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500 py-6 text-center">Tebrikler! Bekleyen herhangi bir aksiyon bulunmuyor.</p>
                )}
              </div>
            </div>
          )}

          {/* TAB 5: RUN HISTORY */}
          {activeViewTab === 'history' && (
            <div className="ai-card-white bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4 min-w-0 max-w-full">
              <div className="border-b border-slate-100 pb-4">
                <h3 className="text-base font-bold text-slate-900">Analiz ve Test Geçmişi</h3>
                <p className="text-xs text-slate-500">
                  Hesabınızda gerçekleştirilen tüm AI Görünürlük çalıştırmaları
                </p>
              </div>

              <div className="space-y-3">
                {runs.map((r) => (
                  <div
                    key={r.runId}
                    onClick={() => {
                      setLatestRun(r);
                      setActiveViewTab('dashboard');
                    }}
                    className={`p-4 rounded-xl border cursor-pointer transition flex items-center justify-between min-w-0 max-w-full ${
                      latestRun?.runId === r.runId
                        ? 'border-indigo-500 bg-indigo-50/50'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                          r.status === 'COMPLETED'
                            ? 'bg-emerald-500'
                            : r.status === 'ABORTED'
                            ? 'bg-amber-500'
                            : 'bg-red-500'
                        }`}
                      ></span>
                      <div className="min-w-0">
                        <span className="text-sm font-bold text-slate-900 block truncate">
                          {new Date(r.completedAt || r.startedAt).toLocaleString('tr-TR')}
                        </span>
                        <span className="text-xs text-slate-500 block truncate">
                          Skor: %{r.overallScore} | Bulunabilirlik: %{r.visibilityRatio} ({r.foundCount}/{r.totalTests})
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <span
                        className={`px-2.5 py-0.5 rounded text-xs font-semibold ${
                          r.status === 'COMPLETED'
                            ? 'bg-emerald-100 text-emerald-800'
                            : r.status === 'ABORTED'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {r.status}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* CREATE / EDIT BUSINESS ENTITY MODAL */}
      {showEntityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-fadeIn">
          <div className="ai-card-white max-w-2xl w-full p-6 sm:p-7 shadow-2xl space-y-5 max-h-[90vh] overflow-y-auto min-w-0">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {entityModalMode === 'create' ? 'Yeni İşletme Varlığı Ekle' : 'İşletme Bilgilerini Güncelle'}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  AI ve Harita servislerinde işletmenizin temsil edileceği resmi profil detayları
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowEntityModal(false)}
                className="text-slate-400 hover:text-slate-700 text-lg font-bold px-2 py-1 rounded-lg"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveEntity} className="ai-form-grid text-sm">
              <div className="ai-form-field-full">
                <label>İşletme / Mekan Adı *</label>
                <input
                  type="text"
                  required
                  placeholder="Örn: Akdeniz Yatçılık & Marin Hizmetleri"
                  value={formBusinessName}
                  onChange={(e) => setFormBusinessName(e.target.value)}
                />
              </div>

              <div className="ai-form-field">
                <label>Şehir *</label>
                <input
                  type="text"
                  required
                  placeholder="Örn: Muğla"
                  value={formCity}
                  onChange={(e) => setFormCity(e.target.value)}
                />
              </div>

              <div className="ai-form-field">
                <label>İlçe / Bölge</label>
                <input
                  type="text"
                  placeholder="Örn: Bodrum"
                  value={formDistrict}
                  onChange={(e) => setFormDistrict(e.target.value)}
                />
              </div>

              <div className="ai-form-field">
                <label>Resmi Web Sitesi</label>
                <input
                  type="url"
                  placeholder="https://example.com"
                  value={formWebsiteUrl}
                  onChange={(e) => setFormWebsiteUrl(e.target.value)}
                />
              </div>

              <div className="ai-form-field">
                <label>Telefon Numarası</label>
                <input
                  type="tel"
                  placeholder="+90 555 123 45 67"
                  value={formPhone}
                  onChange={(e) => setFormPhone(e.target.value)}
                />
              </div>

              <div className="ai-form-field-full">
                <label>Google Haritalar Profil URL'si</label>
                <input
                  type="url"
                  placeholder="https://maps.app.goo.gl/..."
                  value={formGoogleMapsUrl}
                  onChange={(e) => setFormGoogleMapsUrl(e.target.value)}
                />
              </div>

              <div className="ai-form-field-full">
                <label>Verilen Hizmetler (Virgülle ayırın)</label>
                <input
                  type="text"
                  placeholder="Örn: Yat Kiralama, Mavi Tur, Kaptanlı Gulet Turu"
                  value={formServices}
                  onChange={(e) => setFormServices(e.target.value)}
                />
              </div>

              <div className="ai-form-field-full">
                <label>Kısa Açıklama</label>
                <textarea
                  rows={3}
                  placeholder="İşletmenin sunduğu ayrıcalıklar, menü ve deneyimler..."
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                />
              </div>

              <div className="ai-form-field-full flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowEntityModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 font-semibold text-xs hover:bg-slate-50 transition"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs shadow transition"
                >
                  {entityModalMode === 'create' ? 'İşletmeyi Kaydet' : 'Değişiklikleri Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ALERT DIALOGS SYSTEM */}
      {/* 1. Delete Business Entity Confirmation Dialog */}
      <AlertDialog
        open={deleteAlertOpen}
        variant="destructive"
        title="İşletmeyi sil?"
        description="Bu işlem aktif AI işletme profilini devre dışı bırakacak. İşletme verileri geçmiş kayıtlar için korunabilir ancak aktif AI Visibility hakkı serbest bırakılacaktır. İşlem sonrasında yeni bir işletme ekleyebilirsiniz."
        confirmText="İşletmeyi Sil"
        cancelText="Vazgeç"
        loading={isDeletingEntity}
        onConfirm={handleConfirmDeleteEntity}
        onCancel={() => !isDeletingEntity && setDeleteAlertOpen(false)}
      />

      {/* 1b. Archive Business Entity Confirmation Dialog */}
      <AlertDialog
        open={archiveAlertOpen}
        variant="warning"
        title="İşletmeyi Arşivle?"
        description="Bu işletmeyi arşivlemek istediğinize emin misiniz? Arşivledikten sonra yeni bir işletme ekleyebilirsiniz."
        confirmText={isArchivingEntity ? 'Arşivleniyor...' : 'Evet, Arşivle'}
        cancelText="Vazgeç"
        loading={isArchivingEntity}
        onConfirm={handleConfirmArchiveEntity}
        onCancel={() => !isArchivingEntity && setArchiveAlertOpen(false)}
      />

      {/* 2. Second Business Limit Dialog */}
      <AlertDialog
        open={secondBusinessAlertOpen}
        variant="warning"
        title="Bu abonelikte zaten bir işletme aktif"
        description="5.000 TL/yıl AI Visibility aboneliğiniz aynı anda yalnızca bir işletme için kullanılabilir. Yeni bir işletme eklemek için önce mevcut işletmeyi arşivleyin/silin veya yeni bir abonelik alın."
        singleButton
        confirmText="Tamam"
        onCancel={() => setSecondBusinessAlertOpen(false)}
      />

      {/* 3. Task Already Running Dialog */}
      <AlertDialog
        open={runningTaskAlertOpen}
        variant="warning"
        title="Analiz zaten çalışıyor"
        description="Bu hesapta başka bir AI analizi devam ediyor. Mevcut işlem tamamlanmadan yeni bir analiz başlatılamaz."
        singleButton
        confirmText="Tamam"
        onCancel={() => setRunningTaskAlertOpen(false)}
      />

      {/* 4. Analysis Timeout / Aborted Dialog */}
      <AlertDialog
        open={timeoutAlertOpen}
        variant="warning"
        title="Analiz sonlandırıldı"
        description="Analiz beklenen sürede tamamlanamadı ve güvenli şekilde sonlandırıldı. Yeni bir analiz başlatabilirsiniz."
        singleButton
        confirmText="Tamam"
        onCancel={() => setTimeoutAlertOpen(false)}
      />

      {/* 5. API / Engine Error Dialog */}
      <AlertDialog
        open={apiErrorAlertOpen}
        variant="destructive"
        title="AI servisine erişilemedi"
        description={apiErrorMessage || 'Yapay zeka arama motoru servisine şu anda erişilemiyor. Lütfen kısa bir süre sonra tekrar deneyiniz.'}
        singleButton
        confirmText="Tamam"
        onCancel={() => setApiErrorAlertOpen(false)}
      />

      {/* 6. Optimization Apply Confirmation Dialog */}
      <AlertDialog
        open={applyConfirmAlertOpen}
        variant="info"
        title="Bu optimizasyon uygulansın mı?"
        description="Bu değişiklik işletmenizin public web içeriği / teknik SEO yapılandırmasında güncelleme yapacaktır. Seçilen web sitesine ait ayarlara (özel header ve meta verileri) otomatik olarak eklenecektir."
        confirmText="Uygula"
        cancelText="Vazgeç"
        loading={Boolean(applyingRecId)}
        onConfirm={async () => {
          if (pendingApplyRecId) {
            const id = pendingApplyRecId;
            setApplyConfirmAlertOpen(false);
            await handleApplyRecommendation(id);
          }
        }}
        onCancel={() => !applyingRecId && setApplyConfirmAlertOpen(false)}
      />

      {/* SHOPIER PAYMENT MODAL */}
      <ShopierModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        targetPlanId="AI_LOCAL_VISIBILITY"
        planName="AI Yerel İşletme Görünürlüğü"
        amountTRY={5000}
        billingPeriod="Yıllık"
        onSuccess={() => {
          setIsCheckoutOpen(false);
          loadData();
        }}
      />
    </div>
  );
};

export default AIVisibilityView;
